import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Search, Menu, X, ArrowUpRight, Phone } from 'lucide-react';
import { COMPANY, NAV } from '../data/mock';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { setOpen(false); setSearchOpen(false); }, [pathname]);

  const onDarkPage = pathname === '/' || pathname.startsWith('/projects/');
  const onDark = onDarkPage && !scrolled;

  const headerBase = 'fixed top-0 left-0 right-0 z-40 transition-all duration-500';
  const headerStyle = onDark
    ? 'bg-transparent text-white'
    : 'bg-white/95 backdrop-blur-md text-[var(--navy)] shadow-[0_1px_0_rgba(42,45,69,0.06)]';

  return (
    <>
      <header className={`${headerBase} ${headerStyle}`}>
        <div className="container-rs h-20 lg:h-24 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <div className="relative">
              <span className="font-display text-4xl leading-none">B</span>
              <span className="absolute -right-1 bottom-1 w-2.5 h-2.5 rounded-full bg-[var(--coral)]" />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-sans-display text-[15px] font-semibold">Bespoke</span>
              <span className="font-tag opacity-70 text-[10px]">Construction</span>
            </div>
          </Link>

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.path}
                to={n.path}
                className={({ isActive }) => `font-tag relative u-link ${isActive ? 'text-[var(--coral)]' : ''}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            <a href={`tel:${COMPANY.phoneTel}`} className="hidden xl:inline-flex items-center gap-2 font-tag">
              <Phone size={14} />
              <span>{COMPANY.phone}</span>
            </a>
            <button
              onClick={() => setSearchOpen(true)}
              className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border ${onDark ? 'border-white/40 text-white' : 'border-[var(--navy)]/20 text-[var(--navy)]'} hover:border-[var(--coral)] hover:text-[var(--coral)] transition-colors`}
              aria-label="Open search"
            >
              <Search size={14} />
              <span className="font-tag">Search</span>
            </button>
            <Link to="/contact" className="hidden md:inline-flex pill pill-coral">
              <span>Contact</span>
              <ArrowUpRight size={14} />
            </Link>
            <button onClick={() => setOpen(true)} className="lg:hidden p-2" aria-label="Menu"><Menu size={22} /></button>
          </div>
        </div>
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-[var(--navy)]/96 backdrop-blur-md text-white flex flex-col">
          <div className="container-rs h-20 lg:h-24 flex items-center justify-between">
            <span className="font-display text-3xl logo-b">B</span>
            <button onClick={() => setSearchOpen(false)} className="p-2" aria-label="Close"><X size={22} /></button>
          </div>
          <div className="container-rs flex-1 flex flex-col justify-center max-w-3xl">
            <div className="font-tag opacity-70 mb-5">Search the studio</div>
            <div className="flex items-center gap-4 border-b border-white/30 pb-4">
              <Search size={26} className="opacity-70" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try ‘Bristol Street’, ‘Cat B’, ‘BtR’…" className="bg-transparent outline-none flex-1 text-3xl md:text-5xl font-display placeholder:text-white/30" />
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {['Boulevard', 'St Chads', 'Sapphire Court', 'Build-to-Rent', 'Heritage', 'Cat B Fit-out'].map((t) => (
                <button key={t} onClick={() => setQ(t)} className="px-4 py-2 rounded-full border border-white/30 hover:bg-white hover:text-[var(--navy)] transition-colors font-tag">{t}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[var(--navy)] text-white">
          <div className="container-rs h-20 flex items-center justify-between">
            <span className="font-display text-3xl logo-b">B</span>
            <button onClick={() => setOpen(false)} className="p-2" aria-label="Close"><X size={22} /></button>
          </div>
          <nav className="container-rs mt-8 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink key={n.path} to={n.path} className="font-display text-5xl">{n.label}</NavLink>
            ))}
          </nav>
          <div className="container-rs mt-10 font-tag opacity-80 space-y-2">
            <a href={`tel:${COMPANY.phoneTel}`} className="block">{COMPANY.phone}</a>
            <a href={`mailto:${COMPANY.email}`} className="block break-all">{COMPANY.email}</a>
          </div>
        </div>
      )}
    </>
  );
}
